import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

interface FloatingPanelProps {
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  width?: number;
  className?: string;
  children: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const GAP = 8;
const MARGIN = 12;

/**
 * Panel rendered in a portal (so it is never clipped by scrollable tables)
 * and positioned below - or above when there is no room - its anchor.
 */
export const FloatingPanel = ({
  anchorRef,
  open,
  width = 320,
  className,
  children,
  onMouseEnter,
  onMouseLeave,
}: FloatingPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>();

  const computePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    const panelHeight = panel.offsetHeight;
    const panelWidth = Math.min(width, window.innerWidth - MARGIN * 2);

    let left = rect.left + rect.width / 2 - panelWidth / 2;
    left = Math.max(
      MARGIN,
      Math.min(left, window.innerWidth - panelWidth - MARGIN),
    );

    let top = rect.bottom + GAP;
    if (
      top + panelHeight > window.innerHeight - MARGIN &&
      rect.top - GAP - panelHeight > MARGIN
    ) {
      top = rect.top - GAP - panelHeight;
    }

    setPosition({ top, left });
  }, [anchorRef, width]);

  useLayoutEffect(() => {
    if (open) computePosition();
  }, [open, computePosition, children]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('scroll', computePosition, true);
    window.addEventListener('resize', computePosition);
    return () => {
      window.removeEventListener('scroll', computePosition, true);
      window.removeEventListener('resize', computePosition);
    };
  }, [open, computePosition]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="tooltip"
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        width: Math.min(width, window.innerWidth - MARGIN * 2),
      }}
      className={twMerge(
        'fixed z-[1000] rounded-xl border border-gray-200 bg-white shadow-[0_10px_35px_rgba(7,19,54,0.16)] dark-mode:bg-coal-600 dark-mode:border-gray-300',
        className,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>,
    document.body,
  );
};
